import { useTranslation } from "react-i18next";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useFeedbackMutations } from "@stockline/web";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@stockline/ui/components/dialog";
import { FeedbackComposer } from "./FeedbackComposer";

const DIALOG_TAG = "data-feedback-dialog";
const OVERLAY_CLASS = "feedback-dialog-overlay";
/**
 * CSS selector that matches BOTH dialog nodes Radix portals into document.body:
 *   - the content card (tagged via DIALOG_TAG)
 *   - the dim/blur overlay sibling (tagged via OVERLAY_CLASS)
 * html-to-image walks the whole DOM under document.body, so missing either
 * one bakes that node into the captured screenshot.
 */
const CAPTURE_EXCLUDE = `[${DIALOG_TAG}], .${OVERLAY_CLASS}`;

export interface InstantFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InstantFeedbackDialog({
  open,
  onOpenChange,
}: InstantFeedbackDialogProps) {
  const { t } = useTranslation();
  const { createFeedback } = useFeedbackMutations();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // data-feedback-dialog (content) + overlayClassName (backdrop) so
        // html-to-image skips both nodes — otherwise the captured screenshot
        // has Radix's dimmed/blurred backdrop baked in.
        {...{ [DIALOG_TAG]: "" }}
        // Keep this above the AI side panel (z-[100]) so Radix's modal
        // focus layer and the visible dialog stay in the same stack.
        overlayClassName={`${OVERLAY_CLASS} z-[140] bg-black/40`}
        className="z-[150] max-w-[680px] p-0"
      >
        <DialogHeader className="space-y-1.5 border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base tracking-tight">
            <Sparkles size={16} className="text-emerald-600" />
            {t("feedback.instant_title", "Quick feedback")}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t(
              "feedback.instant_subtitle",
              "Tell us what's working, what's not, or what you wish existed.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto px-6 pb-6 pt-3">
          <FeedbackComposer
            compact
            captureExcludeSelector={CAPTURE_EXCLUDE}
            onCancel={() => onOpenChange(false)}
            extraFooterLeft={
              <Link
                to="/feedback"
                onClick={() => onOpenChange(false)}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 underline-offset-4 transition hover:text-emerald-700 hover:underline"
              >
                {t("feedback.view_all", "See all feedback")}
                <ArrowUpRight size={11} />
              </Link>
            }
            onSubmit={async (input) => {
              await createFeedback(input);
              onOpenChange(false);
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
